import React from "react";
import { ArrowLeft, ArrowRight, BookOpenCheck, X } from "lucide-react";
import { clientStorage } from "@/lib/clientStorage";
import { deviceStorage } from "@/lib/deviceStorage";
import { applyWalkthroughStep, endWalkthrough, readWalkthroughStep, WALKTHROUGH_CLIENT, WALKTHROUGH_STEPS } from "@/lib/remindersDemo";
import { getPortalToolPath } from "@/lib/portalToolRoutes";

// The walkthrough's current step, shown under the test mode bar on every LACW page: what
// has just happened to Zara's matter and where to look. Back and Next set her file request,
// matter and diary row to that step, then reload so every tool shows it.
export default function LacwWalkthroughPanel() {
  const index = readWalkthroughStep(deviceStorage);
  if (index === null || !WALKTHROUGH_STEPS[index]) return null;
  const step = WALKTHROUGH_STEPS[index];
  const storage = { client: clientStorage, device: deviceStorage };
  const go = (next) => {
    applyWalkthroughStep(storage, next);
    window.location.reload();
  };
  const end = () => {
    endWalkthrough(deviceStorage);
    window.location.reload();
  };
  const last = index === WALKTHROUGH_STEPS.length - 1;

  return (
    <section aria-label="Walkthrough" className="print:hidden border-b border-sky-200 bg-sky-50 text-slate-900" data-testid="lacw-walkthrough">
      <div className="mx-auto grid max-w-6xl gap-3 px-4 py-3 md:grid-cols-[1fr_auto] md:items-start">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-800">
            <BookOpenCheck className="h-4 w-4" /> Walkthrough · step {index + 1} of {WALKTHROUGH_STEPS.length}
          </p>
          <h2 className="mt-0.5 text-base font-bold">{step.title}</h2>
          <p className="text-sm text-slate-700">{step.happened}</p>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-sm text-slate-700">
            {step.look.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <p className="mt-1.5 flex flex-wrap gap-3 text-xs font-semibold">
            <a href={getPortalToolPath("lacw", "aid-planner")} className="text-sky-800 hover:underline">Open the Aid Planner</a>
            <a href={getPortalToolPath("lacw", "aid-diary")} className="text-sky-800 hover:underline">Open the Aid Status Diary</a>
            <a href={getPortalToolPath("lacw", "reminders-guide")} className="text-sky-800 hover:underline">How reminders work</a>
            <a href="/demo-documents/index.html" target="_blank" rel="noreferrer" className="text-sky-800 hover:underline">Sample letters to upload</a>
            <a href={`${getPortalToolPath("lacw", "actionstep-simulator")}?matter=${WALKTHROUGH_CLIENT.actionstepMatterId}`} className="text-sky-800 hover:underline">Zara in Actionstep (simulator)</a>
          </p>
        </div>
        <div className="flex flex-wrap gap-2 md:justify-end">
          <button type="button" disabled={index === 0} onClick={() => go(index - 1)} className="inline-flex items-center gap-1 rounded-md border border-sky-300 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-sky-100 disabled:opacity-40"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
          {last ? (
            <button type="button" onClick={() => go(0)} className="inline-flex items-center gap-1 rounded-md bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-800">Restart</button>
          ) : (
            <button type="button" onClick={() => go(index + 1)} className="inline-flex items-center gap-1 rounded-md bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-800">Next <ArrowRight className="h-3.5 w-3.5" /></button>
          )}
          <button type="button" onClick={end} aria-label="End the walkthrough" className="inline-flex items-center gap-1 rounded-md border border-sky-300 bg-white px-2 py-1.5 text-xs font-semibold hover:bg-sky-100"><X className="h-3.5 w-3.5" /> End</button>
        </div>
      </div>
    </section>
  );
}
